import { Plus, TrashOutline } from 'heroicons-react'
import { useContext, useEffect, useState } from 'react'
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form'

import useServer from '@/api/useServerMain'
import useServerProduct from '@/api/useServerProducts'
import Input from '@/components/forms/Input'
import Select from '@/components/forms/Select'
import SingleRadio from '@/components/forms/SingleRadio'
import TextArea from '@/components/forms/TextArea'
import AlertContainer from '@/components/misc/AlertContainer'
import Button from '@/components/misc/Button'
import GenericTable, {
  DataTableInterface,
} from '@/components/misc/GenericTable'
import { BtnActions } from '@/components/misc/MultipleActBtn'
import Modal from '@/components/modals/GenericModal'
import { SelectInterface } from '@/interfaces/InterfacesLocal'
import { ProductCommission } from '@/interfaces/ServerInterfaces'
import { useAppSelector } from '@/store/hooks'
import { DetailProductContext } from '../DetailProductContainer'
import { cn } from '@/lib/utils'
import SpinnerLoading from '@/components/misc/SpinnerLoading'

//Commission --------------------------------------------------------------

//Component Commission -----------------------------------------------------------------------------------
interface Commission {
  productCommission?: ProductCommission
  close: () => void
  onSaved?: (_c: ProductCommission) => void
  existingCommissions?: ProductCommission[]
}

const NewCommission = ({
  productCommission,
  close,
  onSaved,
  existingCommissions,
}: Commission) => {
  const { business } = useAppSelector((state) => state.init)
  const { denyRoles } = useServer()
  const { control, handleSubmit, reset, watch, setValue, getValues } = useForm()
  const { addCommission, editCommission, deleteCommission, isFetching } =
    useServerProduct()
  const { product } = useContext(DetailProductContext)
  const [view, setView] = useState('form')

  useEffect(() => {
    if (productCommission) {
      setValue('mode', productCommission.mode)
      const parsedAmount =
        productCommission.amount != null ? Number(productCommission.amount) : ''

      if (productCommission.mode === 'fixed') {
        setValue('fixedAmount', parsedAmount)
      } else {
        setValue('percentAmount', parsedAmount)
      }
      setValue('codeCurrency', productCommission.codeCurrency)
      setValue('priceSystemId', productCommission.priceSystem?.id)
      setValue('description', productCommission.description)
    } else {
      reset({
        mode: 'fixed',
        fixedAmount: '',
        percentAmount: '',
        codeCurrency: '',
        priceSystemId: undefined,
        description: '',
      })
    }
  }, [productCommission, setValue, reset])

  const currenciesList: SelectInterface[] | [] = business
    ? business?.availableCurrencies?.map((currency) => ({
        id: currency.code,
        name: currency.code,
      }))
    : []

  const usedPriceSystemIds =
    existingCommissions?.map((c) => c.priceSystem.id) ?? []

  const priceSystemList: SelectInterface[] | [] = business
    ? business?.priceSystems
        ?.filter(
          (ps) =>
            // permitimos editar: mantener su sistema actual aunque esté "ocupado"
            productCommission?.priceSystem?.id === ps.id ||
            !usedPriceSystemIds.includes(ps.id)
        )
        .map((price) => ({
          id: price.id,
          name: price.name,
        }))
    : []

  interface FormData {
    mode: 'fixed' | 'percent'
    fixedAmount?: string
    percentAmount?: string
    codeCurrency?: string
    priceSystemId: string | number
    description: string
  }

  const onSubmit: SubmitHandler<FieldValues> = (data) => {
    const formData = data as FormData
    const payload = {
      ...formData,
      amount:
        formData.mode === 'fixed'
          ? Number(formData.fixedAmount)
          : Number(formData.percentAmount),
    }

    if (payload['mode'] === 'percent') {
      delete payload.codeCurrency
    }

    delete payload.fixedAmount
    delete payload.percentAmount

    if (productCommission) {
      const callback = (commission: ProductCommission) => {
        const ps = business?.priceSystems?.find(
          (p) => p.id === commission.priceSystemId
        )
        const enriched = {
          ...commission,
          priceSystem: ps ? { id: ps.id, name: ps.name } : { id: 0, name: '' },
        }

        reset()
        close()
        onSaved?.(enriched)
      }
      if (product)
        editCommission(productCommission.id, product.id, payload, callback)
    } else {
      const callback = (commission: ProductCommission) => {
        const ps = business?.priceSystems?.find(
          (p) => p.id === commission.priceSystemId
        )
        const enriched = {
          ...commission,
          priceSystem: ps ? { id: ps.id, name: ps.name } : { id: 0, name: '' },
        }
        reset()
        close()
        onSaved?.(enriched)
      }
      if (product)
        addCommission({ ...payload, productId: product.id }, callback)
    }
  }

  //----------------------------------------------------------------------------------

  //Delete commission ---------------------------------------------------------------
  const deleteCommissionForm = (commissionId?: number, productId?: number) => {
    const callback = () => {
      reset()
      close()
      onSaved?.({ id: commissionId } as ProductCommission)
    }
    deleteCommission(commissionId ?? 0, productId ?? 0, callback)
  }
  //--------------------
  const typeCommission = watch('mode') ?? productCommission?.mode ?? 'fixed'

  useEffect(() => {
    const currentPercentAmount = getValues('percentAmount')
    const currentFixedAmount = getValues('fixedAmount')

    if (typeCommission === 'percent' && currentPercentAmount !== '') {
      setValue('percentAmount', Number(currentPercentAmount))
    }

    if (typeCommission === 'fixed' && currentFixedAmount !== '') {
      setValue('fixedAmount', Number(currentFixedAmount).toFixed(2))
    }
  }, [typeCommission, getValues, setValue])

  return (
    <>
      {view === 'form' && (
        <div className="relative">
          <div className="flex w-full items-center justify-between pe-6 text-center">
            {productCommission ? (
              <h5 className="text-lg font-bold text-gray-600">
                Editar Comisión
              </h5>
            ) : (
              <h5 className="text-lg font-bold text-gray-600">
                Nueva Comisión
              </h5>
            )}
            {productCommission && (
              <Button
                action={() => setView('delete')}
                color="red-400"
                icon={
                  <TrashOutline
                    className="h-5 text-gray-500"
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                    stroke={'#f87171'}
                  />
                }
                outline
              />
            )}
          </div>

          <form onSubmit={handleSubmit(onSubmit)}>
            <div className={'mt-3 w-full'}>
              <div className="mt-4 flex flex-row gap-4">
                <div>
                  <SingleRadio
                    checked={typeCommission === 'fixed'}
                    control={control}
                    label="Monto fijo"
                    name="mode"
                    value={'fixed'}
                  />
                </div>
                <div>
                  <SingleRadio
                    checked={typeCommission === 'percent'}
                    control={control}
                    label="Por porciento"
                    name="mode"
                    rules={{
                      required: 'El campo es obligatorio',
                    }}
                    value={'percent'}
                  />
                </div>
              </div>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-3">
              {typeCommission === 'fixed' && (
                <>
                  <div>
                    <Input
                      control={control}
                      defaultValue={productCommission?.amount ?? ''}
                      label="Monto"
                      name="fixedAmount"
                      numberAsCurrency={{ precision: 2 }}
                      placeholder="Monto"
                      rules={{
                        required:
                          typeCommission === 'fixed'
                            ? 'El campo es obligatorio'
                            : false,
                        min: {
                          value: 0,
                          message: 'El monto no puede ser menor a 0',
                        },
                      }}
                      type="number"
                    />
                  </div>
                  <div>
                    <Select
                      control={control}
                      data={currenciesList}
                      defaultValue={productCommission?.codeCurrency}
                      label="Moneda"
                      name="codeCurrency"
                      rules={{
                        required: 'El campo es obligatorio',
                      }}
                    />
                  </div>
                  {/* col-span-2 ahora sí funciona */}
                  <div className="col-span-2 mt-3">
                    <Select
                      control={control}
                      data={priceSystemList ?? []}
                      defaultValue={productCommission?.priceSystem.id}
                      label="Sistema de precios"
                      name="priceSystemId"
                      rules={{
                        required: 'El campo es obligatorio',
                      }}
                    />
                  </div>
                </>
              )}

              {typeCommission === 'percent' && (
                <>
                  <div>
                    <Input
                      allowComma
                      control={control}
                      defaultValue={
                        productCommission?.mode === 'percent' &&
                        productCommission?.amount != null
                          ? Number(productCommission.amount)
                          : ''
                      }
                      label="Porciento"
                      name="percentAmount"
                      placeholder="Porciento"
                      rules={{
                        required:
                          typeCommission === 'percent'
                            ? 'El campo es obligatorio'
                            : false,
                        max: {
                          value: 100,
                          message: 'El monto no puede ser mayor a 100',
                        },
                        min: {
                          value: 0,
                          message: 'El monto no puede ser menor a 0',
                        },
                      }}
                      type="number"
                    />
                  </div>
                  <div>
                    <Select
                      control={control}
                      data={priceSystemList ?? []}
                      defaultValue={productCommission?.priceSystem.id}
                      label="Sistema de precios"
                      name="priceSystemId"
                      rules={{
                        required: 'El campo es obligatorio',
                      }}
                    />
                  </div>
                </>
              )}
            </div>

            <div className="mt-2 w-full">
              <TextArea
                control={control}
                defaultValue={productCommission?.description}
                label="Descripción"
                name="description"
              />
            </div>

            <div className="inline-flex w-full justify-end gap-2 pt-3">
              {denyRoles(['AUDITOR']) && (
                <Button
                  color="slate-600"
                  loading={isFetching}
                  name={`${productCommission ? 'Actualizar' : 'Crear'}`}
                  type="submit"
                />
              )}
            </div>
          </form>
        </div>
      )}
      {view === 'delete' && (
        <AlertContainer
          loading={isFetching}
          onAction={() =>
            deleteCommissionForm(productCommission?.id ?? 0, product?.id)
          }
          onCancel={() => close()}
          text={`Seguro que desea continuar?`}
          title={`Eliminar ${productCommission?.description ?? ''}`}
        />
      )}
    </>
  )
}
//------------------------------------------------------------------------------------------

//Main Component ---------------------------------------------------------------------------------

const Commission = () => {
  const { product } = useContext(DetailProductContext)
  const { getProductCommissions, isLoading } = useServerProduct()

  const [commissions, setCommissions] = useState<ProductCommission[]>([])

  // useEffect(() => {
  //   // sincroniza el contexto inicial con el estado local
  //   if (productCommissions) {
  //     setCommissions(productCommissions)
  //   }
  // }, [productCommissions])

  useEffect(() => {
    async function fetchData() {
      if (product) {
        const products: ProductCommission[] = await getProductCommissions(
          product.id.toString()
        )
        setCommissions(products)
      }
    }
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id])

  const [commissionModal, setCommissionModal] = useState<{
    state: boolean
    current?: number
  }>({ state: false })

  const commissionTitles = [
    'Descripción',
    'Monto / Porciento',
    'Sistema de precio',
  ]
  const commissionData: DataTableInterface[] = []

  commissions?.forEach((item) => {
    commissionData.push({
      rowId: item.id,
      payload: {
        Descripción: item.description,
        'Monto / Porciento':
          item.mode === 'fixed'
            ? `$${Number(item.amount).toFixed(2)}`
            : `${Number(item.amount).toFixed(2)}%`,
        'Sistema de precio': item.priceSystem?.name ?? '-',
      },
    })
  })

  const rowActionCommission = (id?: number) =>
    setCommissionModal({ state: true, current: id })

  const commissionActions: BtnActions[] = [
    {
      title: 'Insertar comisión',
      icon: (
        <Plus
          className="h-5"
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        />
      ),
      action: () => setCommissionModal({ state: true }),
    },
  ]
  //----------------------------------------------------------------------------------

  return (
    <div>
      <div
        className={cn(
          'h-[34rem] overflow-y-auto rounded-md border border-slate-300 p-5 pr-5',
          'scrollbar-thin scrollbar-thumb-gray-50'
        )}
      >
        <div>
          <h5 className="text-md font-semibold text-gray-700">
            Comisión fija por venta
          </h5>
          <p className={'mb-5 text-sm font-thin italic text-gray-700'}>
            Pago de comisión a comerciales por unidad de producto vendida
          </p>
          {isLoading ? (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <SpinnerLoading text="Cargando datos..." />
            </div>
          ) : (
            <GenericTable
              actions={commissionActions}
              rowAction={rowActionCommission}
              tableData={commissionData}
              tableTitles={commissionTitles}
            />
          )}
        </div>
      </div>
      {/* <div className="flex justify-end py-4">
        {denyRoles(['AUDITOR']) && (
          <Button
            action={fetchUpdateCommissionProduct}
            color="slate-600"
            name="Actualizar"
          />
        )}
      </div> */}

      {commissionModal.state && (
        <Modal
          close={() => setCommissionModal({ state: false })}
          state={commissionModal.state}
        >
          <NewCommission
            close={() => setCommissionModal({ state: false })}
            existingCommissions={commissions}
            onSaved={(commission: ProductCommission) => {
              if (commission && commission.id && !commission.priceSystem) {
                setCommissions((prev) =>
                  prev.filter((c) => c.id !== commission.id)
                )
              } else {
                setCommissions((prev) => {
                  const index = prev.findIndex((c) => c.id === commission.id)
                  if (index !== -1) {
                    const clone = [...prev]
                    clone[index] = commission
                    return clone
                  }
                  return [...prev, commission]
                })
              }
            }}
            productCommission={commissions.find(
              (item) => item.id === commissionModal.current
            )}
          />
        </Modal>
      )}
    </div>
  )
}

//--------------------------------------------------------------------------------------------------

export default Commission
